/**
 * MapToArrayConverter - Utility for converting Map data structures to arrays
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for converting Maps to arrays of objects,
 * which is useful for serialization, API calls, and data transformation.
 */

/**
 * Convert a Map to an array of objects with key-value pairs
 * 
 * Converts a Map<K, V> to an array of objects with named properties.
 * Useful for converting Maps to JSON-serializable formats or API payloads.
 * 
 * @param map - Map to convert
 * @param keyName - Name for the key property in the output objects (default: 'key')
 * @param valueName - Name for the value property in the output objects (default: 'value')
 * @returns Array of objects with key and value properties
 */
export function mapToArray<K, V>(
  map: Map<K, V>,
  keyName: string = 'key',
  valueName: string = 'value'
): Array<Record<string, K | V>> {
  return Array.from(map.entries())
    .map(([key, value]) => ({
      [keyName]: key,
      [valueName]: value
    }));
}

/**
 * Convert a Map<number, string> to an array of objects with page_index and page_hash
 * 
 * Specialized converter for page hash Maps, converting to the format expected
 * by pagination APIs: { page_index: number, page_hash: string }[]
 * 
 * @param pageHashes - Map from page_index to page_hash
 * @returns Array of objects with page_index and page_hash properties
 */
export function pageHashesToArray(
  pageHashes: Map<number, string>
): Array<{ page_index: number; page_hash: string }> {
  return Array.from(pageHashes.entries())
    .map(([page_index, page_hash]) => ({ page_index, page_hash }));
}

/**
 * Convert a Map to an array of tuples [key, value]
 * 
 * Simple conversion that preserves the Map's key-value pairs as tuples.
 * 
 * @param map - Map to convert
 * @returns Array of [key, value] tuples
 */
export function mapToTuples<K, V>(map: Map<K, V>): Array<[K, V]> {
  return Array.from(map.entries());
}

